// T-195 (v1.8.66, Session 46, 09.10.2026): Sperrt der Browser den Speicher (z. B. "Alle Cookies blockieren", Drittanbieter-Speicher
// im eingebetteten Fenster), wirft schon der Zugriff auf window.localStorage einen SecurityError ("Access is denied for this
// document"); die Buchungsseite brach dann mit einer Fehlerseite ab (Befund Sichtprüfung v1.8.64). Statt jeden der rund 70 Zugriffe
// einzeln abzusichern, ersetzt speicherAbsichern beim Start einen gesperrten Speicher durch einen Speicher im Arbeitsspeicher
// (gilt bis zum Neuladen der Seite; Anmeldung und Terminmerker funktionieren innerhalb der Sitzung). Reine Funktionen, getestet in
// sichererSpeicher.test.ts.

export type Speichername = 'localStorage' | 'sessionStorage';

/** Speicher im Arbeitsspeicher mit der Schnittstelle Storage (Werte als Text wie im Browser). */
export class Arbeitsspeicher implements Storage {
  private daten = new Map<string, string>();

  get length(): number {
    return this.daten.size;
  }

  clear(): void {
    this.daten.clear();
  }

  getItem(key: string): string | null {
    const k = String(key);
    return this.daten.has(k) ? (this.daten.get(k) as string) : null;
  }

  key(index: number): string | null {
    return Array.from(this.daten.keys())[index] ?? null;
  }

  removeItem(key: string): void {
    this.daten.delete(String(key));
  }

  setItem(key: string, value: string): void {
    this.daten.set(String(key), String(value));
  }

  [schluessel: string]: any;
}

/**
 * true, wenn der Speicher erreichbar und lesbar ist (Zugriff und getItem). Kein Probe-Schreiben (Review v1.8.66): ein voller, aber
 * lesbarer Speicher bleibt erhalten (sonst ginge die gespeicherte Anmeldung verloren), und andere Fenster erhalten keine
 * storage-Ereignisse.
 */
export function speicherVerfuegbar(fenster: Window, name: Speichername): boolean {
  try {
    const s = fenster[name];
    if (!s) return false;
    s.getItem('__ff_speicherprobe__');
    return true;
  } catch {
    return false;
  }
}

/**
 * Ersetzt gesperrte Speicher des Fensters durch einen Arbeitsspeicher. Rückgabe: die ersetzten Speicher (leer, wenn alles
 * verfügbar ist). Lässt sich ein Speicher nicht ersetzen, bleibt er unverändert (kein neuer Fehler).
 */
export function speicherAbsichern(fenster: Window): Speichername[] {
  const ersetzt: Speichername[] = [];
  (['localStorage', 'sessionStorage'] as Speichername[]).forEach(name => {
    if (speicherVerfuegbar(fenster, name)) return;
    try {
      Object.defineProperty(fenster, name, { configurable: true, enumerable: true, writable: false, value: new Arbeitsspeicher() });
      ersetzt.push(name);
    } catch {
      // nicht ersetzbar: wie bisher
    }
  });
  return ersetzt;
}
